
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ColorThemeOption {
  id: string;
  name: string;
  color_hex?: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useColorThemeOptions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: colorThemes = [], isLoading, error } = useQuery({
    queryKey: ['color-theme-options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_color_themes')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ColorThemeOption[];
    },
  });

  const createColorTheme = useMutation({
    mutationFn: async ({ name, color_hex }: { name: string; color_hex?: string }) => {
      const maxOrder = Math.max(...colorThemes.map(ct => ct.display_order), -1);
      const { data, error } = await supabase
        .from('character_color_themes')
        .insert([{ 
          name, 
          color_hex,
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-theme-options'] });
      toast({
        title: "Success",
        description: "Color theme created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create color theme",
        variant: "destructive",
      });
    },
  });

  const updateColorTheme = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ColorThemeOption> }) => {
      const { data, error } = await supabase
        .from('character_color_themes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-theme-options'] });
      toast({
        title: "Success",
        description: "Color theme updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update color theme",
        variant: "destructive",
      });
    },
  });

  const deleteColorTheme = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_color_themes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-theme-options'] });
      toast({
        title: "Success",
        description: "Color theme deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete color theme",
        variant: "destructive",
      });
    },
  });

  const reorderColorThemes = useMutation({
    mutationFn: async (reorderedItems: { id: string; display_order: number }[]) => {
      const updates = reorderedItems.map(item => 
        supabase
          .from('character_color_themes')
          .update({ display_order: item.display_order })
          .eq('id', item.id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-theme-options'] });
      toast({
        title: "Success",
        description: "Color theme order updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reorder color themes",
        variant: "destructive",
      });
    },
  });

  return {
    colorThemes,
    isLoading,
    error,
    createColorTheme,
    updateColorTheme,
    deleteColorTheme,
    reorderColorThemes,
  };
};
